import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import * as PostProcessing from '@react-three/postprocessing';
import { forwardRef, useRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface OutlineEffectProperties extends EffectProperties {
  edgeStrength: number;
  pulseSpeed: number;
  visibleEdgeColor: string;
  hiddenEdgeColor: string;
  width: number;
  xRay: boolean;
}

// Create a reusable Outline component that can be used in both preview and world
const OutlineComponent = forwardRef<any, { properties: OutlineEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  
  // Create a dummy mesh as a placeholder - moved before conditional return
  // This ensures hooks are always called in the same order
  const dummyMesh = useRef(new THREE.Mesh(
    new THREE.SphereGeometry(1, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
  ));
  
  // IMPORTANT: Return null immediately in preview mode to prevent WebGL errors
  if (isPreview) {
    return null;
  }
  
  const { 
    edgeStrength, 
    pulseSpeed, 
    visibleEdgeColor, 
    hiddenEdgeColor, 
    width,
    xRay,
    blendFunction 
  } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.SCREEN;

  // NOTE: The Outline effect requires a selection of objects to be provided.
  // In a real implementation, this would need to be connected to specific meshes in your scene.
  // This component is a placeholder and will need to be customized for your specific use case.

  // Convert color strings to numbers
  const visibleEdgeColorValue = parseInt(visibleEdgeColor.replace('#', '0x'), 16);
  const hiddenEdgeColorValue = parseInt(hiddenEdgeColor.replace('#', '0x'), 16);

  return (
    <PostProcessing.Outline
      selection={dummyMesh}
      edgeStrength={edgeStrength}
      pulseSpeed={pulseSpeed}
      visibleEdgeColor={visibleEdgeColorValue}
      hiddenEdgeColor={hiddenEdgeColorValue}
      width={width}
      xRay={xRay}
      blendFunction={blendFunctionValue}
    />
  );
});

export const OutlineEffectItem: Effect<OutlineEffectProperties> = {
  id: '',
  name: 'Outline',
  type: 'effect',
  subtype: 'Outline',
  icon: 'Effect',
  properties: {
    edgeStrength: 3,
    pulseSpeed: 0,
    visibleEdgeColor: '#ffffff',
    hiddenEdgeColor: '#ffffff',
    width: 1,
    xRay: true,
    blendFunction: 'SCREEN',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.edgeStrength',
      type: 'number',
      label: 'Edge Strength',
      default: 3,
      min: 0,
      max: 10,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.pulseSpeed',
      type: 'number',
      label: 'Pulse Speed',
      default: 0,
      min: 0,
      max: 2,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.visibleEdgeColor',
      type: 'color',
      label: 'Visible Edge Color',
      default: '#ffffff',
      panel: 'effect',
    },
    {
      key: 'properties.hiddenEdgeColor',
      type: 'color',
      label: 'Hidden Edge Color',
      default: '#ffffff',
      panel: 'effect',
    },
    {
      key: 'properties.width',
      type: 'number',
      label: 'Width',
      default: 1,
      min: 0,
      max: 5,
      step: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.xRay',
      type: 'boolean',
      label: 'X-Ray',
      default: true,
      panel: 'effect',
    },
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'SCREEN',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    }
  ],
  render: (properties: OutlineEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <OutlineComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 